import { formatDateOnly } from '@/shared/composables/dateUtils';

/**
 * Register Detail Registry — 모든 PreSet task type 의 Register 단계 UI 매핑.
 *
 * 사용 흐름:
 *   1. 각 PreSet 의 `<jurisdiction>/preset/register/index.ts` 가 side-effect import 되어
 *      `registerRegisterDetail(taskTypeCd, map)` 호출로 자기 task code 의 매핑 등록.
 *   2. TaskListPage 의 onRegisterClick → `getRegisterDetail(taskTypeCd)` 가 있으면
 *      RegisterDetailDialog 사용 (없으면 기존 confirm 흐름).
 *   3. RegisterDetailDialog 가 매핑의 title + fields 기반으로 TASK_DATA.formData 에서
 *      값을 추출해 generic 하게 렌더.
 *
 * 새 task code 지원 절차:
 *   1) `<jurisdiction>/preset/register/<feature>.ts` 에서 매핑 등록
 *   2) `<jurisdiction>/preset/register/index.ts` 가 import
 *   3) `<jurisdiction>/preset/index.ts` 가 `./register` import
 */

export interface RegisterDetailField {
  /** 화면에 표시될 라벨 — 예: 'Full Name' */
  label: string;
  /**
   * TASK_DATA.formData 에서 값을 찾을 키.
   *   문자열 → 단일 키
   *   배열 → fallback 순서 (camelCase / snake_case / 다른 변형 등)
   */
  key: string | string[];
  /** 옵셔널 포맷터 — 날짜 정규화 / 마스킹 등 */
  format?: (raw: any, source?: Record<string, any>) => string;
}

/** 비서 다명 등 Register 상세 — 탭 패널 단위 */
export interface RegisterDetailTabPanel {
  tabTitle: string;
  rows: RegisterDetailRow[];
}

export interface RegisterDetailRow {
  label: string;
  value: string;
}

export interface RegisterDetailMap {
  /** 섹션 제목 — 예: 'New Director Details' */
  title: string;
  /** 표시할 필드 (값 비어있으면 자동 필터링) */
  fields: RegisterDetailField[];
  /**
   * formData 상의 배열 경로가 있으면, fields 를 인덱스마다 반복 적용해 행을 합친다.
   * 현재 반복 치환 로직은 `secretaries` 만 구현(키의 `secretaries.0.` → `secretaries.{i}.`,
   * `new_secretary_1_` → `new_secretary_{i+1}_`). 그 외 경로(예: `allottees`)는 선언만 허용되고
   * 비반복 경로로 처리됨(추후 일반화 여지).
   */
  repeatArrayPath?: string;
  /** 탭 라벨 커스텀 — 지정하면 repeatTabLabel(arrayPath) 대신 이 값 사용 (예: 'Transfer') */
  repeatTabLabelOverride?: string;
  /**
   * 커스텀 탭 빌더 — 반환 패널이 있으면 flat fields / repeatArrayPath 보다 우선.
   * (예: M32109 Appendix 문서별 BO/SM 다건)
   */
  buildPanels?: (source: Record<string, any>) => RegisterDetailTabPanel[] | null | undefined;
}

const registry: Map<string, RegisterDetailMap> = new Map();
const normalizeTaskTypeCd = (taskTypeCd: string): string => String(taskTypeCd || '').trim().toUpperCase();

/** SG withdraw 계열 — 레지스터 확인 모달에서 내부 사유 코드만 보이는 행은 노출하지 않음 */
const SG_WITHDRAW_REGISTER_DETAIL_CODES = new Set(['B32004', 'B32102', 'B32104']);
const REASON_FOR_CESSATION_LABEL = 'Reason for cessation';
/** 레지스터 확인 모달에서 사유 코드 행 숨김 (코드만 노출되며 UX 잡음) */
const HIDDEN_WITHDRAW_REGISTER_REASON_CODES = new Set(['B12003', 'B12004']);

/** 값에 접두/공백이 붙어도 B12xxx 코드를 잡아 필터·비교에 사용 */
function withdrawReasonRegisterValueToCode(value: unknown): string {
  const s = String(value ?? '').trim().toUpperCase();
  const m = s.match(/\b(B12\d{3})\b/);
  if (m) return m[1];
  return s.replace(/\s+/g, '');
}

function looksLikePlainDateString(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(v) ||
    /^\d{1,2}[-/]\d{1,2}[-/]\d{4}/.test(v)
  );
}

/** Date of Appointment / Cessation date 등 — 세션 dateFormat 으로 통일 표시 */
function registerDetailLabelSuggestsDateOnlyField(label: string): boolean {
  return /(date|appointment|cessation|birth|resolution|effective|expir)/i.test(label);
}

function formatRegisterDetailRowValue(label: string, value: string): string {
  if (!value?.trim()) return value;
  if (!registerDetailLabelSuggestsDateOnlyField(label) || !looksLikePlainDateString(value)) {
    return value;
  }
  const formatted = formatDateOnly(value);
  return formatted?.trim() ? formatted : value;
}

export function registerRegisterDetail(taskTypeCd: string, map: RegisterDetailMap): void {
  if (!taskTypeCd || !map) return;
  registry.set(normalizeTaskTypeCd(taskTypeCd), map);
}

export function getRegisterDetail(taskTypeCd: string): RegisterDetailMap | undefined {
  if (!taskTypeCd) return undefined;
  return registry.get(normalizeTaskTypeCd(taskTypeCd));
}

export function hasRegisterDetail(taskTypeCd: string): boolean {
  return registry.has(normalizeTaskTypeCd(taskTypeCd));
}

export function getRegisteredTaskCodes(): string[] {
  return Array.from(registry.keys());
}

// ───────────────────────────── helpers ─────────────────────────────

function getNestedValue(source: Record<string, any>, path: string): any {
  if (!source || !path) return undefined;
  if (!path.includes('.')) return source?.[path];
  return path.split('.').reduce<any>((acc, part) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[part];
  }, source);
}

/** repeat: 매핑에 쓰인 `<arrayPath>.0.*` 를 행 인덱스에 맞게 치환 (secretaries 는 new_secretary_1_ 도). */
function rewriteRepeatKeySegment(key: string, index: number, arrayPath: string): string {
  let k = String(key).replace(new RegExp(`${arrayPath}\\.0\\.`, 'g'), `${arrayPath}.${index}.`);
  if (arrayPath === 'secretaries') {
    k = k.replace(/new_secretary_1_/g, `new_secretary_${index + 1}_`);
  }
  return k;
}

function mapFieldsForRepeatIndex(
  fields: RegisterDetailField[],
  index: number,
  arrayPath: string
): RegisterDetailField[] {
  return fields.map((f) => {
    const keys = Array.isArray(f.key) ? f.key : [f.key];
    const mapped = keys.map((k) => rewriteRepeatKeySegment(k, index, arrayPath));
    return { ...f, key: Array.isArray(f.key) ? mapped : mapped[0] };
  });
}

/** arrayPath → 탭 라벨 단수형 (secretaries→Secretary, directors→Director, allottees→Allottee …). */
function repeatTabLabel(arrayPath: string): string {
  const cap = (w: string) => (w ? w[0].toUpperCase() + w.slice(1) : w);
  if (arrayPath === 'secretaries') return 'Secretary';
  if (arrayPath === 'directors') return 'Director';
  if (arrayPath.endsWith('ies')) return cap(arrayPath.slice(0, -3) + 'y');
  if (arrayPath.endsWith('s')) return cap(arrayPath.slice(0, -1));
  return cap(arrayPath);
}

/**
 * formData 에서 RegisterDetailField 에 맞춰 값 추출.
 *   key 가 배열이면 순서대로 fallback.
 *   값이 객체 ({ value, label, code }) 면 value/label 추출.
 */
export function extractFieldValue(
  formData: Record<string, any>,
  field: RegisterDetailField
): string {
  const keys = Array.isArray(field.key) ? field.key : [field.key];
  let raw: any;
  for (const k of keys) {
    const v = getNestedValue(formData, k);
    if (v !== null && v !== undefined && v !== '') {
      raw = v;
      break;
    }
  }
  if (raw === null || raw === undefined) return '';
  if (field.format) {
    return field.format(raw, formData);
  }
  let str: string;
  if (typeof raw === 'object') {
    str = raw.value || raw.label || raw.code || '';
  } else {
    str = String(raw);
  }
  return str;
}

/**
 * TASK_DATA(JSON string or object) 에서 formData 추출 후
 * 등록된 매핑으로 [{label, value}] 배열을 만들어 반환.
 * 값 없는 행은 자동 제외.
 */
export function extractRegisterDetailRows(
  taskTypeCd: string,
  taskData: string | Record<string, any> | undefined | null
): {
  title: string;
  rows: RegisterDetailRow[];
  /** 비서 2명 이상일 때만 설정. UI 탭용; rows 는 하위 호환을 위해 전체 행을 이어붙인 값 */
  tabs?: RegisterDetailTabPanel[];
} | null {
  const map = getRegisterDetail(taskTypeCd);
  if (!map) return null;
  let parsed: any = {};
  if (taskData) {
    if (typeof taskData === 'object') parsed = taskData;
    else {
      try { parsed = JSON.parse(taskData); } catch { /* ignore */ }
    }
  }
  const formDataRaw = parsed?.formData || {};
  // preset 폼은 { presetForm: {...} } 로 감싸 저장되므로(transfers·companyName·날짜 등이 그 안에 있음),
  //   presetForm 이 있으면 상위로 병합해 매핑 key(예: 'transfers.0.transferorName', 'companyName')가 해석되게 한다.
  const preset = (formDataRaw.presetForm && typeof formDataRaw.presetForm === 'object')
    ? formDataRaw.presetForm
    : null;
  const formData = preset ? { ...formDataRaw, ...preset } : formDataRaw;
  const source = { ...parsed, ...formData, formData };

  if (typeof map.buildPanels === 'function') {
    const customPanels = map.buildPanels(source) || [];
    const tabPanels = customPanels
      .map((p) => ({
        tabTitle: String(p.tabTitle || '').trim() || 'Details',
        rows: (Array.isArray(p.rows) ? p.rows : [])
          .map((r) => ({
            label: String(r.label || '').trim(),
            value: formatRegisterDetailRowValue(String(r.label || ''), String(r.value ?? '').trim()),
          }))
          .filter((r) => r.label && r.value),
      }))
      .filter((p) => p.rows.length > 0);
    if (tabPanels.length > 0) {
      return {
        title: map.title,
        rows: tabPanels.flatMap((t) => t.rows),
        tabs: tabPanels.length > 1 ? tabPanels : undefined,
      };
    }
  }

  if (map.repeatArrayPath) {
    const arrayPath = map.repeatArrayPath;
    const arr = getNestedValue(formData, arrayPath);
    if (Array.isArray(arr) && arr.length > 0) {
      const tabLabel = map.repeatTabLabelOverride || repeatTabLabel(arrayPath);
      const tabPanels: RegisterDetailTabPanel[] = [];
      for (let i = 0; i < arr.length; i++) {
        const fieldsI = mapFieldsForRepeatIndex(map.fields, i, arrayPath);
        const chunk = fieldsI
          .map((f) => ({ label: f.label, value: extractFieldValue(source, f) }))
          .filter((r) => r.value);
        if (chunk.length === 0) continue;
        tabPanels.push({
          tabTitle: `${tabLabel} ${i + 1}`,
          rows: chunk,
        });
      }
      if (tabPanels.length > 0) {
        const rows = tabPanels.flatMap((t) => t.rows);
        return {
          title: map.title,
          rows,
          tabs: tabPanels.length > 1 ? tabPanels : undefined,
        };
      }
    }
  }

  const rows = map.fields
    .map((f) => {
      const raw = extractFieldValue(source, f);
      return { label: f.label, value: formatRegisterDetailRowValue(f.label, raw) };
    })
    .filter((r) => r.value)
    .filter((r) => {
      if (!SG_WITHDRAW_REGISTER_DETAIL_CODES.has(normalizeTaskTypeCd(taskTypeCd))) return true;
      if (r.label !== REASON_FOR_CESSATION_LABEL) return true;
      const code = withdrawReasonRegisterValueToCode(r.value);
      return !HIDDEN_WITHDRAW_REGISTER_REASON_CODES.has(code);
    });
  return { title: map.title, rows };
}
